import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../api/client';

const DAYS_KO = ['일', '월', '화', '수', '목', '금', '토'];

function GrowthChart({ data }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);

  const maxUsers = Math.max(...data.map((d) => d.users), 1);
  const maxSchedules = Math.max(...data.map((d) => d.schedules), 1);
  const W = 100;
  const H = 40;
  const pad = { top: 4, bottom: 6, left: 4, right: 4 };
  const cw = W - pad.left - pad.right;
  const ch = H - pad.top - pad.bottom;

  const getX = (i) => pad.left + (data.length === 1 ? cw / 2 : (i / (data.length - 1)) * cw);
  const getY = (val, maxVal) => pad.top + ch - (val / maxVal) * ch;

  const toPath = (key, maxVal) => {
    return data.map((d, i) => {
      const x = getX(i);
      const y = getY(d[key], maxVal);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ');
  };

  const toAreaPath = (key, maxVal) => {
    const baseline = pad.top + ch;
    const linePart = data.map((d, i) => {
      const x = getX(i);
      const y = getY(d[key], maxVal);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ');
    const lastX = getX(data.length - 1);
    const firstX = getX(0);
    return `${linePart} L${lastX},${baseline} L${firstX},${baseline} Z`;
  };

  const formatLabel = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const formatTooltipDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const step = Math.max(1, Math.floor(data.length / 5));
  const labelIndices = data.map((_, i) => i).filter((i) => i % step === 0 || i === data.length - 1);

  const findNearestIndex = useCallback((clientX) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const relX = (clientX - rect.left) / rect.width;
    const idx = Math.round(relX * (data.length - 1));
    return Math.max(0, Math.min(data.length - 1, idx));
  }, [data.length]);

  const handleMouseMove = (e) => {
    setHoverIdx(findNearestIndex(e.clientX));
  };

  const handleTouchMove = (e) => {
    if (e.touches.length > 0) {
      setHoverIdx(findNearestIndex(e.touches[0].clientX));
    }
  };

  const handleLeave = () => setHoverIdx(null);

  const hovered = hoverIdx !== null ? data[hoverIdx] : null;
  const hoverX = hoverIdx !== null ? getX(hoverIdx) : 0;

  // tooltip pixel position (percentage for CSS left)
  const tooltipLeftPct = hoverIdx !== null ? (hoverX / W) * 100 : 0;

  return (
    <div className="relative select-none" ref={containerRef}>
      {/* Y-axis scale labels */}
      <div className="flex justify-between text-[10px] text-sub-text mb-1.5 px-0.5">
        <span className="flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A90D9]" />
          <span className="text-[#4A90D9] font-medium">max {maxUsers}명</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="text-violet-500 font-medium">max {maxSchedules}건</span>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
        </span>
      </div>

      {/* SVG Chart */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-44 cursor-crosshair"
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleLeave}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleLeave}
      >
        <defs>
          <linearGradient id="areaUsers" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4A90D9" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#4A90D9" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="areaSchedules" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((r) => (
          <line key={r} x1={pad.left} x2={W - pad.right}
            y1={pad.top + ch - r * ch} y2={pad.top + ch - r * ch}
            stroke="#f0f0f0" strokeWidth="0.12" strokeDasharray="0.8,0.4" />
        ))}

        {/* Area fills */}
        <path d={toAreaPath('users', maxUsers)} fill="url(#areaUsers)" />
        <path d={toAreaPath('schedules', maxSchedules)} fill="url(#areaSchedules)" />

        {/* Lines */}
        <path d={toPath('users', maxUsers)} fill="none" stroke="#4A90D9" strokeWidth="0.45" strokeLinejoin="round" strokeLinecap="round" />
        <path d={toPath('schedules', maxSchedules)} fill="none" stroke="#8b5cf6" strokeWidth="0.45" strokeLinejoin="round" strokeLinecap="round" />

        {/* Data points (non-hovered: small) */}
        {data.map((d, i) => {
          const x = getX(i);
          const isHovered = i === hoverIdx;
          return (
            <g key={i} style={{ transition: 'opacity 0.15s' }} opacity={hoverIdx === null || isHovered ? 1 : 0.35}>
              <circle cx={x} cy={getY(d.users, maxUsers)}
                r={isHovered ? '1' : '0.5'} fill={isHovered ? '#fff' : '#4A90D9'}
                stroke={isHovered ? '#4A90D9' : 'none'} strokeWidth={isHovered ? '0.35' : '0'}
                style={{ transition: 'r 0.15s, fill 0.15s' }} />
              <circle cx={x} cy={getY(d.schedules, maxSchedules)}
                r={isHovered ? '1' : '0.5'} fill={isHovered ? '#fff' : '#8b5cf6'}
                stroke={isHovered ? '#8b5cf6' : 'none'} strokeWidth={isHovered ? '0.35' : '0'}
                style={{ transition: 'r 0.15s, fill 0.15s' }} />
            </g>
          );
        })}

        {/* Hover vertical guide line */}
        {hoverIdx !== null && (
          <line
            x1={hoverX} x2={hoverX}
            y1={pad.top} y2={pad.top + ch}
            stroke="#cbd5e1" strokeWidth="0.18" strokeDasharray="0.6,0.4"
          />
        )}
      </svg>

      {/* Hover Tooltip (HTML overlay for better styling) */}
      {hovered && (
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: `${tooltipLeftPct}%`,
            top: '8px',
            transform: tooltipLeftPct > 75 ? 'translateX(-90%)' : tooltipLeftPct < 25 ? 'translateX(-10%)' : 'translateX(-50%)',
          }}
        >
          <div className="bg-charcoal/90 backdrop-blur-sm text-white rounded-lg px-3 py-2 shadow-lg text-[11px] whitespace-nowrap">
            <p className="font-medium text-white/70 mb-1">{formatTooltipDate(hovered.date)}</p>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A90D9]" />
                <span className="font-semibold">{hovered.users}명</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400" />
                <span className="font-semibold">{hovered.schedules}건</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* X-axis labels */}
      <div className="flex justify-between mt-1.5 px-0.5">
        {labelIndices.map((i) => (
          <span key={i} className={`text-[10px] transition-colors duration-150 ${
            i === hoverIdx ? 'text-charcoal font-semibold' : 'text-sub-text'
          }`}>{formatLabel(data[i].date)}</span>
        ))}
      </div>
    </div>
  );
}

function BarChart({ chartData, maxCount }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div className="bg-white rounded-2xl shadow-card p-5">
      <h3 className="text-base font-bold text-charcoal mb-4">최근 7일 예약 추이</h3>
      <div className="flex items-end gap-2 h-40">
        {chartData.map((day, i) => {
          const total = (day.success || 0) + (day.failed || 0);
          const successH = maxCount > 0 ? ((day.success || 0) / maxCount) * 100 : 0;
          const failedH = maxCount > 0 ? ((day.failed || 0) / maxCount) * 100 : 0;
          const date = new Date(day.date);
          const isHovered = i === hoverIdx;
          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center gap-1 relative cursor-pointer"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onTouchStart={() => setHoverIdx(i)}
              onTouchEnd={() => setHoverIdx(null)}
            >
              {/* Hover Tooltip */}
              {isHovered && total > 0 && (
                <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                  <div className="bg-charcoal/90 backdrop-blur-sm text-white rounded-lg px-2.5 py-1.5 shadow-lg text-[11px] whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {(day.success || 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-mint" />
                          <span className="font-semibold">{day.success}</span>
                        </span>
                      )}
                      {(day.failed || 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-coral" />
                          <span className="font-semibold">{day.failed}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-1.5 h-1.5 bg-charcoal/90 rotate-45 -mt-0.5" />
                  </div>
                </div>
              )}
              <span className={`text-xs transition-colors duration-150 ${
                isHovered ? 'text-charcoal font-semibold' : 'text-sub-text'
              }`}>{total || ''}</span>
              <div className="w-full flex flex-col justify-end" style={{ height: '120px' }}>
                {failedH > 0 && (
                  <div
                    className={`w-full rounded-t transition-all duration-150 ${
                      isHovered ? 'bg-coral' : 'bg-coral/60'
                    }`}
                    style={{ height: `${failedH}%` }}
                  />
                )}
                {successH > 0 && (
                  <div
                    className={`w-full transition-all duration-150 ${failedH === 0 ? 'rounded-t' : ''} ${
                      isHovered ? 'bg-mint' : 'bg-mint/60'
                    }`}
                    style={{ height: `${successH}%` }}
                  />
                )}
                {total === 0 && (
                  <div className="w-full bg-border rounded-t" style={{ height: '4px' }} />
                )}
              </div>
              <span className={`text-xs transition-colors duration-150 ${
                isHovered ? 'text-charcoal font-semibold' : 'text-sub-text'
              }`}>
                {DAYS_KO[date.getDay()]}
              </span>
              <span className={`text-[10px] transition-colors duration-150 ${
                isHovered ? 'text-charcoal/70' : 'text-sub-text/70'
              }`}>
                {String(date.getMonth() + 1).padStart(2, '0')}/{String(date.getDate()).padStart(2, '0')}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3">
        <span className="flex items-center gap-1.5 text-xs text-sub-text">
          <span className="inline-block w-3 h-3 rounded-sm bg-mint/70" />
          성공
        </span>
        <span className="flex items-center gap-1.5 text-xs text-sub-text">
          <span className="inline-block w-3 h-3 rounded-sm bg-coral/70" />
          실패
        </span>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, colorClass }) {
  return (
    <div className={`rounded-2xl p-5 ${colorClass}`}>
      <p className="text-sm text-sub-text mb-1">{label}</p>
      <p className="text-3xl font-extrabold text-charcoal">{value ?? '-'}</p>
      {sub && <p className="text-xs text-sub-text mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const result = await adminAPI.getStats();
      if (!result.success) throw new Error(result.error || '통계를 불러올 수 없습니다');
      return result.stats;
    },
  });

  const chartData = data?.recentLogs || [];
  const maxCount = Math.max(...chartData.map((d) => (d.success || 0) + (d.failed || 0)), 1);

  if (isLoading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
        <p className="mt-2 text-sub-text">로딩 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
        <p className="text-sm text-coral">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-charcoal">대시보드</h2>
        <p className="text-sm text-sub-text mt-1">시스템 전체 현황</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="총 사용자"
          value={data?.totalUsers?.toLocaleString()}
          colorClass="bg-tayo-light"
        />
        <StatCard
          label="활성 사용자"
          value={data?.activeUsers?.toLocaleString()}
          colorClass="bg-emerald-50"
        />
        <StatCard
          label="활성 스케줄"
          value={data?.activeSchedules?.toLocaleString()}
          colorClass="bg-violet-50"
        />
        <StatCard
          label="성공률"
          value={data?.successRate != null ? `${data.successRate}%` : '-'}
          colorClass="bg-amber-50"
        />
      </div>

      {/* Bar Chart */}
      {chartData.length > 0 && <BarChart chartData={chartData} maxCount={maxCount} />}

      {/* Growth Timeline */}
      {data?.growthTimeline?.length > 1 && (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <h3 className="text-base font-bold text-charcoal mb-4">가입 추이</h3>
          <GrowthChart data={data.growthTimeline} />
          <div className="flex items-center gap-4 mt-3">
            <span className="flex items-center gap-1.5 text-xs text-sub-text">
              <span className="inline-block w-3 h-3 rounded-sm bg-tayo" />
              사용자
            </span>
            <span className="flex items-center gap-1.5 text-xs text-sub-text">
              <span className="inline-block w-3 h-3 rounded-sm bg-violet-400" />
              스케줄
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
